/**
 * CustomCamposData — seletores de data e hora TEMATIZADOS (react-datepicker), no lugar
 * dos `<input type=date/time>` nativos (cujo popup do navegador não segue o tema).
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  ATENÇÃO: "DATA RÁPIDA" NÃO É NATIVO DO ADMINUX NEM DO REACT-DATEPICKER.  ║
 * ║  É CRIAÇÃO NOSSA (Vittorio + Claude, 26/07/2026), montada por cima deles.  ║
 * ║  Se algo estranho acontecer num campo de data, ESTE arquivo é o primeiro   ║
 * ║  suspeito — não o tema. Doc: .claude/docs/tema/01-campos-data-rapida.md    ║
 * ║  Convenção entre projetos: .claude-hub/convencao-campos-data-rapida.md     ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * O tema já estiliza o react-datepicker inteiro em `assets/scss/_calendar.scss`.
 * Aqui embrulhamos ele num contrato controlado em strings ('YYYY-MM-DD' e 'HH:MM').
 *
 * Decisões (pedido do Vittorio, 22/07):
 * - Input **editável**: dá pra DIGITAR a data/hora (não fica preso nas sugestões de
 *   15 em 15). O dropdown é atalho, não a única via.
 * - **Ícone à direita** (calendário / relógio), num `input-group` do tema.
 * - locale pt-BR (dd/MM/aaaa, hora 24h).
 * - `portalId` → popup dentro da área da Agenda (`#agenda-dp-portal`): não corta no
 *   modal e não some na Tela Cheia (portal pro body sumiria em fullscreen).
 *
 * DATA RÁPIDA (26/07) — digitar a data sem abrir o calendário:
 *   `7` → dia 7 do mês/ano corrente | `0103` → 01/03 do ano corrente
 *   `010325` → 01/03/2025 | `01032025` → idem
 *   Regra: **dia aceita 1 ou 2 dígitos, mês exige 2, ano exige 2 ou 4** — logo, sem
 *   separador só valem 1, 2, 4, 6 e 8 dígitos. Com separador ("1/3/25") a exigência
 *   cai, porque já não há ambiguidade.
 *
 *   Como ligar/desligar:
 *   - global: a constante `DATA_RAPIDA` abaixo (trocar `true` por `false` devolve
 *     TODOS os campos ao comportamento antigo, sem tocar em nenhuma tela);
 *   - por campo: `<CustomCampoData ... rapida={false} />`.
 *   O código antigo continua vivo em `CampoDataClassico`/`CampoHoraClassico`.
 */
import React, { forwardRef, useMemo, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('pt-BR', ptBR);

/** Container do popup — a Agenda renderiza `<div id={DP_PORTAL_ID}>` dentro da sua área. */
export const DP_PORTAL_ID = 'agenda-dp-portal';

/** INTERRUPTOR GLOBAL da Data Rápida. `false` devolve o app ao comportamento antigo. */
export const DATA_RAPIDA = true;

/**
 * `103` → 01/03? Pela regra (mês sempre com 2 dígitos) 3 dígitos seria inequívoco,
 * mas o Vittorio preferiu recusar — `012` dá nó na cabeça e o ganho é pequeno.
 * Trocar pra `true` habilita em todos os campos de uma vez.
 */
export const ACEITA_3_DIGITOS = false;

// ============================================================ parsers (Data Rápida)

type ParseData = { data?: Date; erro?: string; vazio?: boolean };

const anoCheio = (n: number, digitos: number) => (digitos === 2 ? 2000 + n : n);

/** dd / ddMM / ddMMaa / ddMMaaaa — ou com separador, aí cada parte vale como veio. */
export function parseDataRapida(texto: string, hoje = new Date(), aceita3 = ACEITA_3_DIGITOS): ParseData {
  const s = (texto || '').trim();
  if (!s) return { vazio: true };

  let d: number, m: number, a: number;

  if (/\D/.test(s)) {
    const p = s.split(/\D+/).filter(Boolean);
    if (p.length < 1 || p.length > 3) return { erro: 'formato não reconhecido' };
    if (p[2] && p[2].length !== 2 && p[2].length !== 4)
      return { erro: 'ano precisa de 2 ou 4 dígitos' };
    d = +p[0];
    m = p.length > 1 ? +p[1] : hoje.getMonth() + 1;
    a = p.length > 2 ? anoCheio(+p[2], p[2].length) : hoje.getFullYear();
  } else {
    const n = s.length;
    if (n <= 2) {
      d = +s;
      m = hoje.getMonth() + 1;
      a = hoje.getFullYear();
    } else if (n === 3) {
      if (!aceita3) return { erro: '3 dígitos: mês precisa de 2 dígitos' };
      d = +s.slice(0, 1);
      m = +s.slice(1);
      a = hoje.getFullYear();
    } else if (n === 4) {
      d = +s.slice(0, 2);
      m = +s.slice(2);
      a = hoje.getFullYear();
    } else if (n === 6) {
      d = +s.slice(0, 2);
      m = +s.slice(2, 4);
      a = anoCheio(+s.slice(4), 2);
    } else if (n === 8) {
      d = +s.slice(0, 2);
      m = +s.slice(2, 4);
      a = +s.slice(4);
    } else {
      return { erro: `${n} dígitos: mês precisa de 2 e ano de 2 ou 4` };
    }
  }

  if (m < 1 || m > 12) return { erro: 'mês inválido' };
  if (d < 1 || d > 31) return { erro: 'dia inválido' };
  const dt = new Date(a, m - 1, d);
  if (dt.getDate() !== d || dt.getMonth() !== m - 1) return { erro: 'essa data não existe' };
  return { data: dt };
}

/** Hora: minuto SEMPRE 2 dígitos → 1-2 díg = hora cheia, 3 = h+mm, 4 = hh+mm. */
export function parseHoraRapida(texto: string): { hora?: string; erro?: string; vazio?: boolean } {
  const s = (texto || '').trim();
  if (!s) return { vazio: true };
  let h: number, mi: number;
  if (/\D/.test(s)) {
    const p = s.split(/\D+/).filter(Boolean);
    h = +p[0];
    mi = p.length > 1 ? +p[1] : 0;
  } else if (s.length <= 2) {
    h = +s;
    mi = 0;
  } else if (s.length === 3) {
    h = +s.slice(0, 1);
    mi = +s.slice(1);
  } else if (s.length === 4) {
    h = +s.slice(0, 2);
    mi = +s.slice(2);
  } else {
    return { erro: 'até 4 dígitos' };
  }
  if (Number.isNaN(h) || Number.isNaN(mi) || h > 23 || mi > 59) return { erro: 'hora inválida' };
  return { hora: `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}` };
}

/** Separa "26/07 a 30/08" em duas partes. O separador de intervalo exige espaço em
 *  volta, senão o hífen colidiria com o separador de data ("01-07-2025"). */
function partesDoIntervalo(s: string): string[] {
  return s
    .replace(/\s+(?:-|–|—|a|até|à)\s+/gi, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

/** INTERVALO. A 2ª data herda mês/ano da 1ª: `0107 31` → 01/07 a 31/07. */
export function parseIntervaloRapido(
  texto: string,
  hoje = new Date(),
  aceita3 = ACEITA_3_DIGITOS,
): { inicio?: Date; fim?: Date; invertido?: boolean; erro?: string; vazio?: boolean } {
  const s = (texto || '').trim();
  if (!s) return { vazio: true };

  const partes = partesDoIntervalo(s);
  if (partes.length > 2) return { erro: 'esperava duas datas' };

  const p1 = parseDataRapida(partes[0], hoje, aceita3);
  if (p1.erro) return { erro: `início: ${p1.erro}` };
  if (!p1.data) return { erro: 'início inválido' };
  if (partes.length === 1) return { inicio: p1.data, fim: p1.data };

  const p2 = parseDataRapida(partes[1], p1.data, aceita3);
  if (p2.erro) return { erro: `fim: ${p2.erro}` };
  if (!p2.data) return { erro: 'fim inválido' };

  if (p2.data < p1.data) return { inicio: p2.data, fim: p1.data, invertido: true };
  return { inicio: p1.data, fim: p2.data };
}

/** DATA + HORA na mesma caixa: "2607 1430" → 26/07 14:30. Hora é opcional. */
export function parseDataHoraRapida(
  texto: string,
  hoje = new Date(),
  aceita3 = ACEITA_3_DIGITOS,
): ParseData {
  const s = (texto || '').trim();
  if (!s) return { vazio: true };
  const partes = s.split(/\s+/).filter(Boolean);
  if (partes.length > 2) return { erro: 'esperava data e hora' };

  const pd = parseDataRapida(partes[0], hoje, aceita3);
  if (pd.erro) return { erro: pd.erro };
  if (!pd.data) return { erro: 'data inválida' };
  if (partes.length === 1) return { data: pd.data };

  const ph = parseHoraRapida(partes[1]);
  if (ph.erro) return { erro: `hora: ${ph.erro}` };
  const [h, mi] = ph.hora!.split(':').map(Number);
  const dt = new Date(pd.data);
  dt.setHours(h, mi, 0, 0);
  return { data: dt };
}

/** MÊS/ANO: `7` → 07 do ano corrente | `0726` → 07/2026 | `072026`. */
export function parseMesAnoRapido(texto: string, hoje = new Date()): ParseData {
  const s = (texto || '').trim();
  if (!s) return { vazio: true };
  let m: number, a: number;
  if (/\D/.test(s)) {
    const p = s.split(/\D+/).filter(Boolean);
    if (p.length > 2) return { erro: 'formato não reconhecido' };
    if (p[1] && p[1].length !== 2 && p[1].length !== 4)
      return { erro: 'ano precisa de 2 ou 4 dígitos' };
    m = +p[0];
    a = p[1] ? anoCheio(+p[1], p[1].length) : hoje.getFullYear();
  } else if (s.length <= 2) {
    m = +s;
    a = hoje.getFullYear();
  } else if (s.length === 4) {
    m = +s.slice(0, 2);
    a = anoCheio(+s.slice(2), 2);
  } else if (s.length === 6) {
    m = +s.slice(0, 2);
    a = +s.slice(2);
  } else {
    return { erro: `${s.length} dígitos: mês com 2 e ano com 2 ou 4` };
  }
  if (m < 1 || m > 12) return { erro: 'mês inválido' };
  return { data: new Date(a, m - 1, 1) };
}

/** ANO: 2 dígitos → 20xx, 4 dígitos → tal qual. */
export function parseAnoRapido(texto: string): ParseData {
  const s = (texto || '').trim();
  if (!s) return { vazio: true };
  if (/\D/.test(s)) return { erro: 'só dígitos' };
  if (s.length !== 2 && s.length !== 4) return { erro: 'ano com 2 ou 4 dígitos' };
  return { data: new Date(anoCheio(+s, s.length), 0, 1) };
}

// ============================================================ conversões

/** 'YYYY-MM-DD' → Date local (meia-noite), ou null. */
function isoParaData(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const [a, m, d] = iso.split('-').map(Number);
  if (!a || !m || !d) return null;
  return new Date(a, m - 1, d);
}

/** Date → 'YYYY-MM-DD' no fuso LOCAL (nunca toISOString, que vira UTC e erra o dia). */
function dataParaIso(d: Date | null): string {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/** 'HH:MM' → Date (data qualquer, só a hora importa), ou null. */
function horaParaData(hhmm: string | null | undefined): Date | null {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h)) return null;
  const d = new Date(2000, 0, 1);
  d.setHours(h, m || 0, 0, 0);
  return d;
}

const dd = (n: number) => String(n).padStart(2, '0');
const fmtData = (d: Date | null) =>
  d ? `${dd(d.getDate())}/${dd(d.getMonth() + 1)}/${d.getFullYear()}` : '';
const fmtHora = (d: Date | null) => (d ? `${dd(d.getHours())}:${dd(d.getMinutes())}` : '');
const fmtDataHora = (d: Date | null) => (d ? `${fmtData(d)} ${fmtHora(d)}` : '');
const fmtMesAno = (d: Date | null) => (d ? `${dd(d.getMonth() + 1)}/${d.getFullYear()}` : '');
const fmtAno = (d: Date | null) => (d ? String(d.getFullYear()) : '');
const isoParaTexto = (iso: string) => fmtData(isoParaData(iso));
const mesmoDia = (a: Date | null, b: Date | null) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// ============================================================ input do tema

/**
 * Input do tema com ícone à direita (input-group).
 *
 * Sem `aoConfirmar` funciona como sempre funcionou (clicar na caixa abre o
 * calendário). COM `aoConfirmar` entra em modo Data Rápida:
 * - o calendário abre **só pelo ícone** — clicar na caixa é pra escrever, então
 *   duplo clique seleciona o texto e o popup não entra na frente;
 * - Blur/Enter confirmam o que foi digitado;
 * - `aoTab` deixa o Tab "avançar dentro do campo" (usado no intervalo).
 */
const InputIcone = forwardRef<HTMLInputElement, any>(function InputIcone(
  {
    value,
    onClick,
    onChange,
    onKeyDown,
    onBlur,
    placeholder,
    icone,
    label,
    id,
    aoConfirmar,
    sujo,
    aoAlternarCalendario,
    refIcone,
    aoTab,
    pequeno,
  },
  ref,
) {
  const rapida = !!aoConfirmar;

  const campo = (
    <input
      ref={ref}
      id={id}
      type="text"
      className="form-control"
      value={value ?? ''}
      onClick={rapida ? undefined : onClick}
      onChange={onChange}
      onBlur={(e) => {
        if (rapida && sujo) aoConfirmar(e.target.value);
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (rapida) {
          if (e.key === 'Escape') {
            aoAlternarCalendario?.(false);
            return;
          }
          // Tab pode ser "avance dentro do campo" em vez de "saia do campo" —
          // é o que faz o intervalo digitável funcionar (1ª data, Tab, 2ª data).
          if (e.key === 'Tab' && !e.shiftKey && aoTab) {
            const alvo = e.target as HTMLInputElement;
            if (aoTab(alvo.value, alvo)) {
              e.preventDefault();
              return;
            }
          }
          // Enter só é nosso quando há texto digitado — senão roubaríamos o Enter
          // de quem está navegando o calendário pelas setas.
          if (e.key === 'Enter' && sujo) {
            e.preventDefault();
            e.stopPropagation();
            aoConfirmar((e.target as HTMLInputElement).value);
            (e.target as HTMLInputElement).blur();
            return;
          }
        }
        onKeyDown?.(e);
      }}
      // Com label flutuante o placeholder tem que ser um espaço: o Bootstrap usa
      // :placeholder-shown pra decidir se o label fica grande no meio ou pequeno
      // em cima. Um placeholder com texto ("dd/mm/aaaa") apareceria por baixo do
      // label. Sem label, o texto de dica volta a valer.
      placeholder={label ? ' ' : placeholder}
      autoComplete="off"
      // Cofre de senha (Dashlane e cia.) põe o ícone dele no canto do campo e
      // rouba o clique do calendário. Acontece porque o nosso campo é
      // `type="text"` — que é justamente o que permite digitar `0103` —, e para
      // o cofre todo input de texto é candidato a login; o `type="date"` nativo
      // do tema eles nem tocam. `autocomplete="off"` NÃO basta: os cofres
      // ignoram por política (senão qualquer site desligaria o preenchimento
      // deles). O que eles respeitam é um atributo próprio, um por marca.
      data-form-type="other" /* Dashlane */
      data-lpignore="true" /* LastPass */
      data-1p-ignore="true" /* 1Password */
      data-bwignore="true" /* Bitwarden */
    />
  );

  return (
    <div className={`input-group${pequeno ? ' input-group-sm' : ''}`}>
      {label ? (
        // .form-floating DENTRO do .input-group é o arranjo que o Bootstrap 5
        // suporta pra casar label flutuante com addon de ícone.
        <div className="form-floating">
          {campo}
          <label htmlFor={id}>{label}</label>
        </div>
      ) : (
        campo
      )}
      <span
        ref={refIcone}
        className="input-group-text bg-transparent text-theme-1"
        role="button"
        onClick={rapida ? undefined : onClick}
        // mousedown + preventDefault: não tira o foco do input ao abrir o calendário
        onMouseDown={
          rapida
            ? (e) => {
                e.preventDefault();
                aoAlternarCalendario?.();
              }
            : undefined
        }
      >
        <i className={`bi ${icone}`}></i>
      </span>
    </div>
  );
});

/**
 * Toda a mecânica da Data Rápida: rascunho do texto, abertura controlada e o
 * corte do parse ao vivo do react-datepicker.
 *
 * ARMADILHA (custou o bug do "só funciona na 2ª vez"): NÃO dá pra deixar o
 * react-datepicker gerenciar o texto digitado e só corrigir no blur — ele guarda
 * o texto num state interno (`inputValue`) e o descarta no blur, e essa corrida
 * se resolve diferente quando o campo ainda está vazio (`selected == null`), que
 * é justo a 1ª digitação. Por isso QUEM MANDA NO TEXTO SOMOS NÓS: passamos
 * `value` (string), que tem prioridade sobre o state interno dele, e cortamos o
 * parse ao vivo com `onChangeRaw` + preventDefault.
 */
function useDataRapida(confirmar: (t: string) => boolean) {
  const [rascunho, setRascunho] = useState<string | null>(null);
  const [aberto, setAberto] = useState(false);
  const refIcone = useRef<HTMLSpanElement>(null);

  const propsInput = {
    sujo: rascunho !== null,
    refIcone,
    aoConfirmar: (t: string) => {
      setRascunho(null);
      return confirmar(t);
    },
    aoAlternarCalendario: (v?: boolean) => setAberto((a) => v ?? !a),
  };

  const propsPicker = {
    open: aberto,
    onClickOutside: (e: any) => {
      if (refIcone.current?.contains(e.target as Node)) return;
      setAberto(false);
    },
    onChangeRaw: (e: any) => {
      const alvo = e?.target as HTMLInputElement | undefined;
      if (alvo && typeof alvo.value === 'string') setRascunho(alvo.value);
      e?.preventDefault();
    },
  };

  return { texto: (doValor: string) => rascunho ?? doValor, setRascunho, aberto, setAberto, propsInput, propsPicker };
}

/** Props comuns a todos os pickers — `popperPlacement` é CORREÇÃO (o popup nascia
 *  centralizado por ancorar no input-group), então vale também no modo clássico. */
const PROPS_COMUNS = {
  locale: 'pt-BR',
  wrapperClassName: 'd-block',
  portalId: DP_PORTAL_ID,
  popperPlacement: 'bottom-start' as const,
};

// ============================================================ data

type PropsData = {
  valor: string; // 'YYYY-MM-DD' | ''
  onChange: (iso: string) => void;
  id?: string;
  label?: string;
};

/** Comportamento anterior à Data Rápida — preservado pra reverter sem perder nada. */
function CampoDataClassico({ valor, onChange, id, label }: PropsData) {
  return (
    <DatePicker
      id={id}
      selected={isoParaData(valor)}
      onChange={(d) => onChange(dataParaIso(d))}
      dateFormat="dd/MM/yyyy"
      placeholderText="dd/mm/aaaa"
      {...PROPS_COMUNS}
      customInput={<InputIcone icone="bi-calendar-event" placeholder="dd/mm/aaaa" label={label} id={id} />}
    />
  );
}

function CampoDataRapida({ valor, onChange, id, label }: PropsData) {
  const c = useDataRapida((t) => {
    const r = parseDataRapida(t);
    if (r.vazio || r.erro) {
      onChange(''); // não entendeu → LIMPA (reverter em silêncio engana)
      return false;
    }
    onChange(dataParaIso(r.data!));
    return true;
  });

  return (
    <DatePicker
      id={id}
      value={c.texto(isoParaTexto(valor))}
      selected={isoParaData(valor)}
      onChange={(d) => {
        c.setRascunho(null);
        c.setAberto(false);
        onChange(dataParaIso(d));
      }}
      dateFormat="dd/MM/yyyy"
      placeholderText="dd/mm/aaaa"
      {...PROPS_COMUNS}
      {...c.propsPicker}
      customInput={
        <InputIcone icone="bi-calendar-event" placeholder="dd/mm/aaaa" label={label} id={id} {...c.propsInput} />
      }
    />
  );
}

/** `label` liga o rótulo flutuante (label DENTRO da caixa) — o padrão de formulário
 *  deste app. Sem ele, o campo fica com a dica no placeholder, como antes.
 *  `rapida` liga/desliga a Data Rápida só neste campo (o padrão vem de DATA_RAPIDA). */
export function CustomCampoData({ rapida = DATA_RAPIDA, ...p }: PropsData & { rapida?: boolean }) {
  return rapida ? <CampoDataRapida {...p} /> : <CampoDataClassico {...p} />;
}

// ============================================================ hora

type PropsHora = {
  valor: string; // 'HH:MM' | '' (vazio = sem hora / dia inteiro)
  onChange: (hhmm: string) => void;
  id?: string;
  label?: string;
};

function CampoHoraClassico({ valor, onChange, id, label }: PropsHora) {
  return (
    <DatePicker
      id={id}
      selected={horaParaData(valor)}
      onChange={(d) => onChange(fmtHora(d))}
      showTimeSelect
      showTimeSelectOnly
      timeIntervals={15}
      timeCaption="Hora"
      dateFormat="HH:mm"
      timeFormat="HH:mm"
      placeholderText="Sem hora"
      {...PROPS_COMUNS}
      customInput={<InputIcone icone="bi-clock" placeholder="Sem hora" label={label} id={id} />}
    />
  );
}

function CampoHoraRapida({ valor, onChange, id, label }: PropsHora) {
  const c = useDataRapida((t) => {
    const r = parseHoraRapida(t);
    if (r.vazio || r.erro) {
      onChange('');
      return false;
    }
    onChange(r.hora!);
    return true;
  });

  return (
    <DatePicker
      id={id}
      value={c.texto(valor)}
      selected={horaParaData(valor)}
      onChange={(d) => {
        c.setRascunho(null);
        c.setAberto(false);
        onChange(fmtHora(d));
      }}
      showTimeSelect
      showTimeSelectOnly
      timeIntervals={15}
      timeCaption="Hora"
      dateFormat="HH:mm"
      timeFormat="HH:mm"
      placeholderText="Sem hora"
      {...PROPS_COMUNS}
      {...c.propsPicker}
      customInput={<InputIcone icone="bi-clock" placeholder="Sem hora" label={label} id={id} {...c.propsInput} />}
    />
  );
}

export function CustomCampoHora({ rapida = DATA_RAPIDA, ...p }: PropsHora & { rapida?: boolean }) {
  return rapida ? <CampoHoraRapida {...p} /> : <CampoHoraClassico {...p} />;
}

// ============================================================ campos novos
// Não existiam antes da Data Rápida — nascem com ela e não têm versão clássica
// (não há nada em produção pra preservar).

/** Data + hora na mesma caixa: digite `2607 1430`. */
export function CustomCampoDataHora({
  valor,
  onChange,
  id,
  label,
}: {
  valor: Date | null;
  onChange: (d: Date | null) => void;
  id?: string;
  label?: string;
}) {
  const c = useDataRapida((t) => {
    const r = parseDataHoraRapida(t);
    if (r.vazio || r.erro) {
      onChange(null);
      return false;
    }
    onChange(r.data!);
    return true;
  });

  return (
    <DatePicker
      id={id}
      value={c.texto(fmtDataHora(valor))}
      selected={valor}
      onChange={(d) => {
        c.setRascunho(null);
        onChange(d);
      }}
      showTimeSelect
      timeIntervals={15}
      timeCaption="Hora"
      dateFormat="dd/MM/yyyy HH:mm"
      timeFormat="HH:mm"
      placeholderText="dd/mm/aaaa hh:mm"
      {...PROPS_COMUNS}
      {...c.propsPicker}
      customInput={
        <InputIcone icone="bi-calendar-event" placeholder="dd/mm/aaaa hh:mm" label={label} id={id} {...c.propsInput} />
      }
    />
  );
}

/** Mês/ano — útil em filtro de relatório ("mês de referência"). Digite `7` ou `0726`. */
export function CustomCampoMesAno({
  valor,
  onChange,
  id,
  label,
}: {
  valor: Date | null;
  onChange: (d: Date | null) => void;
  id?: string;
  label?: string;
}) {
  const c = useDataRapida((t) => {
    const r = parseMesAnoRapido(t);
    if (r.vazio || r.erro) {
      onChange(null);
      return false;
    }
    onChange(r.data!);
    return true;
  });

  return (
    <DatePicker
      id={id}
      value={c.texto(fmtMesAno(valor))}
      selected={valor}
      onChange={(d) => {
        c.setRascunho(null);
        c.setAberto(false);
        onChange(d);
      }}
      showMonthYearPicker
      dateFormat="MM/yyyy"
      placeholderText="mm/aaaa"
      {...PROPS_COMUNS}
      {...c.propsPicker}
      customInput={<InputIcone icone="bi-calendar3" placeholder="mm/aaaa" label={label} id={id} {...c.propsInput} />}
    />
  );
}

/** Ano. Digite `26` ou `2026`. */
export function CustomCampoAno({
  valor,
  onChange,
  id,
  label,
}: {
  valor: Date | null;
  onChange: (d: Date | null) => void;
  id?: string;
  label?: string;
}) {
  const c = useDataRapida((t) => {
    const r = parseAnoRapido(t);
    if (r.vazio || r.erro) {
      onChange(null);
      return false;
    }
    onChange(r.data!);
    return true;
  });

  return (
    <DatePicker
      id={id}
      value={c.texto(fmtAno(valor))}
      selected={valor}
      onChange={(d) => {
        c.setRascunho(null);
        c.setAberto(false);
        onChange(d);
      }}
      showYearPicker
      dateFormat="yyyy"
      placeholderText="aaaa"
      {...PROPS_COMUNS}
      {...c.propsPicker}
      customInput={<InputIcone icone="bi-calendar3" placeholder="aaaa" label={label} id={id} {...c.propsInput} />}
    />
  );
}

// ============================================================ intervalo

/** Atalhos do range picker do tema, traduzidos (o original é "Today / Last 7 Days"). */
const ATALHOS: { rotulo: string; calc: () => [Date, Date] }[] = [
  { rotulo: 'Hoje', calc: () => [new Date(), new Date()] },
  {
    rotulo: 'Ontem',
    calc: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return [d, new Date(d)];
    },
  },
  {
    rotulo: 'Últimos 7 dias',
    calc: () => {
      const i = new Date();
      i.setDate(i.getDate() - 6);
      return [i, new Date()];
    },
  },
  {
    rotulo: 'Últimos 30 dias',
    calc: () => {
      const i = new Date();
      i.setDate(i.getDate() - 29);
      return [i, new Date()];
    },
  },
  {
    rotulo: 'Este mês',
    calc: () => {
      const h = new Date();
      return [new Date(h.getFullYear(), h.getMonth(), 1), new Date(h.getFullYear(), h.getMonth() + 1, 0)];
    },
  },
  {
    rotulo: 'Mês passado',
    calc: () => {
      const h = new Date();
      return [new Date(h.getFullYear(), h.getMonth() - 1, 1), new Date(h.getFullYear(), h.getMonth(), 0)];
    },
  },
];

/**
 * Intervalo de datas — calendário duplo com atalhos, no estilo do dashboard do tema.
 *
 * SEM Aplicar/Cancelar (decisão de 26/07): o 2º clique fecha o intervalo e aplica,
 * igual ao Tab depois de digitar as duas datas — teclado e mouse coerentes.
 * Clicar numa data com um período já definido significa "novo intervalo": a data
 * final anterior é descartada na hora.
 *
 * O `onChange` só dispara com o intervalo COMPLETO — a tela que filtra não vê
 * estados pela metade.
 */
export function CustomCampoIntervalo({
  inicio,
  fim,
  onChange,
  id,
  label,
  pequeno,
}: {
  inicio: string; // 'YYYY-MM-DD' | ''
  fim: string;
  onChange: (inicio: string, fim: string) => void;
  id?: string;
  label?: string;
  /** `input-group-sm`, sem rótulo flutuante: para barra de filtros, ao lado
   *  de selects `form-select-sm` (despesas, 24/09). */
  pequeno?: boolean;
}) {
  const [pendente, setPendente] = useState<[Date | null, Date | null]>([null, null]);
  // true = o Tab já avançou pra 2ª data; o próximo Tab sai do campo
  const [esperandoFim, setEsperandoFim] = useState(false);

  const c = useDataRapida((t) => {
    setEsperandoFim(false);
    const r = parseIntervaloRapido(t);
    if (r.vazio || r.erro) {
      onChange('', '');
      return false;
    }
    onChange(dataParaIso(r.inicio!), dataParaIso(r.fim!));
    return true;
  });

  /** Tab com só a 1ª data: completa pra "15/07/2026 - " e ESPERA a 2ª, sem sair. */
  const aoTab = (t: string, input: HTMLInputElement) => {
    if (esperandoFim) return false;
    const texto = (t || '').trim();
    if (!texto) return false;
    const partes = partesDoIntervalo(texto);
    if (partes.length >= 2) return false;
    const p1 = parseDataRapida(partes[0]);
    if (!p1.data) return false;
    const novo = `${fmtData(p1.data)} - `;
    c.setRascunho(novo);
    setEsperandoFim(true);
    requestAnimationFrame(() => input.setSelectionRange(novo.length, novo.length));
    return true;
  };

  const abrir = (v?: boolean) => {
    setPendente([isoParaData(inicio), isoParaData(fim)]);
    c.propsInput.aoAlternarCalendario(v);
  };

  const [pi, pf] = pendente;
  const textoDoValor = inicio ? `${isoParaTexto(inicio)} - ${isoParaTexto(fim || inicio)}` : '';
  // Com o calendário ABERTO o campo mostra a seleção em andamento, não o valor
  // aplicado — senão clicar nos dias não mexe em nada visível e o controle parece
  // quebrado.
  const textoDoPendente = pi ? `${fmtData(pi)} - ${pf ? fmtData(pf) : ''}` : '';
  const textoBase = c.aberto && pi ? textoDoPendente : textoDoValor;

  // o container não pode ser recriado a cada render (remontaria o calendário a
  // cada tecla), então fica estável e conversa por ref
  const api = useRef({ atalho: (_: () => [Date, Date]) => {}, rodape: '', ativo: '' });
  api.current = {
    atalho: (calc) => {
      const [i, f] = calc();
      setPendente([i, f]);
      onChange(dataParaIso(i), dataParaIso(f));
      c.setAberto(false);
    },
    rodape: !pi
      ? 'Clique na data inicial'
      : !pf
        ? `${fmtData(pi)} — agora clique na data final`
        : `${fmtData(pi)} - ${fmtData(pf)}`,
    ativo:
      ATALHOS.find((a) => {
        const [i, f] = a.calc();
        return mesmoDia(i, pi) && mesmoDia(f, pf);
      })?.rotulo ?? 'Personalizado',
  };

  const Container = useMemo(
    () =>
      function ContainerDoIntervalo({ className, children }: { className?: string; children?: React.ReactNode }) {
        const { rodape, ativo } = api.current;
        return (
          <div className={`card adminuiux-card overflow-hidden ${className ?? ''}`}>
            <div className="d-flex flex-nowrap">
              <div className="d-flex flex-column py-2 border-end" style={{ minWidth: 155 }}>
                {ATALHOS.map((a) => (
                  <button
                    key={a.rotulo}
                    type="button"
                    className={`btn btn-sm text-start rounded-0 ${
                      ativo === a.rotulo ? 'btn-theme-1 text-white' : 'btn-link'
                    }`}
                    style={{ fontSize: '0.875rem' }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => api.current.atalho(a.calc)}
                  >
                    {a.rotulo}
                  </button>
                ))}
                <span
                  className={`btn btn-sm text-start rounded-0 disabled ${
                    ativo === 'Personalizado' ? 'btn-theme-1 text-white' : 'btn-link'
                  }`}
                  style={{ fontSize: '0.875rem' }}
                >
                  Personalizado
                </span>
              </div>
              <div className="position-relative d-flex flex-nowrap">{children}</div>
            </div>
            <div className="d-flex align-items-center px-3 py-2 border-top">
              <span className="small text-body text-opacity-75">{rodape}</span>
            </div>
          </div>
        );
      },
    [],
  );

  return (
    <DatePicker
      id={id}
      value={c.texto(textoBase)}
      selectsRange
      monthsShown={2}
      startDate={pi ?? undefined}
      endDate={pf ?? undefined}
      selected={pi}
      shouldCloseOnSelect={false}
      onChange={(datas) => {
        const [i, f] = datas as [Date | null, Date | null];
        c.setRascunho(null);
        setPendente([i, f]);
        if (i && f) {
          onChange(dataParaIso(i), dataParaIso(f));
          c.setAberto(false);
        }
      }}
      calendarContainer={Container}
      dateFormat="dd/MM/yyyy"
      placeholderText="dd/mm/aaaa - dd/mm/aaaa"
      {...PROPS_COMUNS}
      {...c.propsPicker}
      customInput={
        <InputIcone
          icone="bi-calendar3"
          placeholder="dd/mm/aaaa - dd/mm/aaaa"
          label={label}
          id={id}
          {...c.propsInput}
          aoAlternarCalendario={abrir}
          aoTab={aoTab}
          pequeno={pequeno}
        />
      }
    />
  );
}
